<template>
  <div class="timesheet layout">
    <!-- <shift-modal ref="shiftModal" :can-edit="canEdit" @save="saveShifts"></shift-modal> -->
    <positions-modal ref="positionsModal" :positions="positions" @apply="updatePositions"></positions-modal>
    <workers-modal ref="workersModal" :positions="positions" @apply="addWorkers"></workers-modal>
    <columns-modal
      ref="columnsModal"
      :columns="columnsList"
      :default-selected="defaultColumns"
      :blocked-columns="['worker', 'days']"
      :columns-count="2"
      @apply="saveUserColumns"
    />
    <remove-timesheet-modal ref="removeTimesheetModal" @remove="onRemove"></remove-timesheet-modal>

    <div class="layout-header">
      <div class="layout-title">
        <template v-if="currentTimesheet"> <b>Табель</b> {{ timesheetName }} </template>
        <template v-else><b>Новый табель</b></template>
      </div>
      <div class="layout-header-actions">
        <div v-if="currentTimesheet && currentTimesheet.id" class="layout-header-action">
          <c-button-dropdown
            icon="arrow"
            type="dropdown"
            class="actions"
            label="Действия"
            :dropdown="{
              position: 'left',
              items: buttonDropdownItems,
            }"
            @remove="removeTimesheet"
          />
        </div>
        <div class="layout-header-action">
          <c-button2 :responsive="true" size="small" label="К списку табелей" icon="back" @click="toList" />
        </div>
      </div>
    </div>

    <div class="timesheet__content">
      <template v-if="timesheetDataLoading">
        <Loader :active="true" label="Данные загружаются" :height="434"></Loader>
      </template>

      <template v-else>
        <timesheet-filters
          v-if="canEdit"
          class="timesheet-filters"
          :server-error="serverError"
          :filters="filters"
          :submitted="submitted"
          @reset="resetFilters"
          @apply="applyFilters"
        ></timesheet-filters>

        <div v-if="inited" class="timesheet-chart">
          <div class="timesheet-chart__actions">
            <div class="timesheet-chart__actions-group">
              <c-button2
                v-if="positions.length"
                class="timesheet-chart__action timesheet-chart__add-workers"
                size="small"
                label="Добавить сотрудников"
                :disabled="loading"
                @click="openWorkersModal()"
              />

              <codes-dictionary
                class="timesheet-chart__action timesheet-chart__config-codes"
                :disabled="loading"
              ></codes-dictionary>
            </div>

            <div class="timesheet-chart__actions-group">
              <c-switcher2
                v-model="shift"
                type="group"
                class="h-switcher timesheet-chart__action timesheet-chart__shifts"
                :items="[
                  { label: 'Все смены', value: 'all' },
                  { label: 'Дневная', value: 'day' },
                  { label: 'Ночная', value: 'night' },
                ]"
                :disabled="loading"
              />
              <c-button2
                class="timesheet-chart__action timesheet-chart__config-positions"
                size="small"
                label="Должности"
                :disabled="loading"
                @click="openPositionsModal"
              />
              <c-button2
                class="timesheet-chart__action timesheet-chart__config-columns"
                size="small"
                label="Настроить столбцы"
                icon="cog"
                :disabled="loading"
                @click="configColumns"
              />
            </div>
          </div>

          <Loader v-if="loading" :active="loading" label="Производится расчет" :height="434"></Loader>
          <template v-if="!loading">
            <scrolled-table class="timesheet-chart__table" :fix-first="true" :fix-last="true">
              <table class="h-table">
                <thead>
                  <tr>
                    <th
                      v-for="column in columns"
                      :key="column.id"
                      class="timesheet-chart-column"
                      :class="'timesheet-chart-column__' + column.id"
                    >
                      <div class="h-table__header">
                        <template v-if="column.id === 'days'">
                          <div class="h-table__col-title">
                            {{ monthName }}
                          </div>

                          <div class="timeline">
                            <div
                              v-for="day in chartData.total.days"
                              :key="day.key"
                              class="day"
                              :class="{
                                conflict: day.hasConflict,
                              }"
                            >
                              {{ day.day }}
                            </div>
                          </div>
                        </template>

                        <template v-else>
                          <div class="h-table__col-title">
                            <div class="title">
                              {{
                                [
                                  column.shortName,
                                  column.unit === "ruble" && "฿",
                                  column.unit === "ruble/unit" && "฿/шт.",
                                  column.unit === "HH:MM" && "чч:мм",
                                ]
                                  .filter(Boolean)
                                  .join(", ")
                              }}
                            </div>
                          </div>
                          <div v-if="column.id !== 'name'" class="h-table__actions">
                            <c-button
                              :id="`table-column-button-description-${column.id}`"
                              size="small"
                              icon="info"
                              no-border
                            />
                            <b-popover
                              placement="top"
                              triggers="hover"
                              :target="`table-column-button-description-${column.id}`"
                            >
                              {{ column.name }}
                            </b-popover>
                          </div>
                        </template>
                      </div>
                    </th>
                  </tr>
                </thead>
                <template v-for="position in positionsList">
                  <tr :key="position.code" class="position-name">
                    <template v-for="column in columns">
                      <td :key="column.id" :class="'position-name__' + column.id">
                        <table-position-cell
                          :column="column.id"
                          :position="position"
                          :data="chartData.positions[position.code] || {}"
                          @addWorkers="addWorkersToPosition(position)"
                        ></table-position-cell>
                      </td>
                    </template>
                  </tr>
                  <transition :key="position.code + '-workers'" name="slide">
                    <tbody :key="position.code + '-workers'" class="position-workers">
                      <template v-for="worker in (chartData.positions[position.code] || []).workers">
                        <tr v-if="shiftDayOrNight(worker)" :key="worker.id" class="worker-data">
                          <td v-for="column in columns" :key="column.id" :class="'worker-data__' + column.id">
                            <table-cell
                              :column="column.id"
                              :worker="worker"
                              :position="position"
                              :filial="currentTimesheet && currentTimesheet.filial.id"
                              @onDayClick="onDayClick"
                              @remove="removeWorker"
                            ></table-cell>
                          </td>
                        </tr>
                      </template>
                    </tbody>
                  </transition>
                </template>
                <tfoot>
                  <tr>
                    <td
                      v-for="column in columns"
                      :key="column.id"
                      class="timesheet-total-column"
                      :class="'timesheet-total-column__' + column.id"
                    >
                      <table-total-cell
                        :column="column.id"
                        :workers="workersList"
                        :stats="chartData.total"
                      ></table-total-cell>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </scrolled-table>

            <div v-if="canEdit" class="timesheet__actions">
              <c-button2 class="timesheet__reset" label="Отменить" size="large" @click="reset"></c-button2>
              <c-button2
                class="timesheet__save"
                label="Сохранить"
                :disabled="saving"
                color="primary"
                size="large"
                @click="save"
              ></c-button2>
            </div>
          </template>
        </div>
      </template>
    </div>

    <div v-if="feedback" class="timesheet-feedback" :class="feedback.status">
      <div class="timesheet-feedback__wrapper">
        <div class="timesheet-feedback__icon">
          <template v-if="feedback.status === 'success'">
            <svg width="14" height="10" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M4.47288 9.68401C4.08236 9.29348 4.08236 8.66032 4.47288 8.2698L12.2929 0.449632C12.6835 0.0591074 13.3166 0.059107 13.7072 0.449631C14.0977 0.840156 14.0977 1.47332 13.7072 1.86384L5.8871 9.68401C5.49657 10.0745 4.86341 10.0745 4.47288 9.68401Z"
                fill="#12C971"
              />
              <path
                d="M5.8871 9.68401C6.27762 9.29348 6.27756 8.66032 5.88704 8.2698L1.70711 4.08989C1.31658 3.69937 0.683417 3.69937 0.292893 4.08989C-0.097631 4.48042 -0.0976311 5.11358 0.292893 5.50411L4.47288 9.68401C4.86341 10.0745 5.49657 10.0745 5.8871 9.68401Z"
                fill="#12C971"
              />
            </svg>
          </template>
          <template v-if="feedback.status === 'error'">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="10" cy="10" r="9" stroke="#FF238D" stroke-width="2" />
              <circle cx="10" cy="14" r="1" fill="#FF238D" />
              <rect x="9" y="5" width="2" height="6" rx="1" fill="#FF238D" />
            </svg>
          </template>
        </div>
        {{ feedback.text }}
      </div>
    </div>

    <top-button root=".layout"></top-button>
  </div>
</template>

<script>
import moment from "moment";
import CButton2 from "@/components/controls2/Button";
import CButton from "@/components/controls/button";
import CButtonDropdown from "@/components/controls/button-dropdown";
import CSwitcher2 from "@/components/controls2/Switcher";
import TopButton from "@/components/top-button";
import Loader from "@/components/loader";
import ScrolledTable from "@/components/scrolled-table";
import ColumnsModal from "@/components/modals/table-columns/modal/";

import TableCell from "./components/cell";
import TablePositionCell from "./components/position-cell";
import TableTotalCell from "./components/total-cell";
// import ShiftModal from "@/pages/timesheet/components/shift-modal";
import CodesDictionary from "./components/codes-dictionary";
import TimesheetFilters from "./components/filters";
import WorkersModal from "./components/workers-modal";
import PositionsModal from "./components/positions-modal";
import RemoveTimesheetModal from "../components/remove-timesheet-modal";

import { MONTHS } from "@/utils/date/months";
import { Column } from "@/entities/structures/column";
import { Timesheet } from "../entities/timesheet";
import { CalculatedDay, Day, Shift } from "../entities/shift-models";

const defaultPeriod = moment().startOf("month").format("YYYY-MM-DD");

export default {
  components: {
    CButton2,
    CButton,
    CButtonDropdown,

    CSwitcher2,
    TopButton,
    Loader,
    ScrolledTable,
    ColumnsModal,

    TableCell,
    TablePositionCell,
    TableTotalCell,

    RemoveTimesheetModal,

    // ShiftModal,
    WorkersModal,
    CodesDictionary,
    TimesheetFilters,
    PositionsModal,
  },

  data() {
    return {
      id: null,
      currentTimesheet: null,
      timesheetDataLoading: false,

      canEdit: false,

      inited: false,
      loading: false,
      submitted: false,
      saving: false,

      columnsList: [],
      visibleColumns: [],

      filters: {
        period: defaultPeriod,
        filial: null,
      },

      savedPeriod: defaultPeriod,
      savedFilial: "",

      positions: [],
      visiblePositions: [],

      workersByPositions: [], // данные сотрудников
      shift: "all",

      savedTimesheet: [],
      timesheet: {}, // данные по сменам

      calculations: {},

      timesheetCreatedError: "",

      feedback: false,
    };
  },

  computed: {
    buttonDropdownItems() {
      const items = [
        {
          label: "Выгрузить в Google-таблицы",
          icon: "google",
          link: `/index.php?r=google-sheets/create&user_id=${this.$store.state.userId}&table=timesheet&id=${this.$route.params.id}`,
        },
        {
          label: "Распечатать",
          icon: "print",
          link: `/?r=redesign#/timesheets/print/${this.currentTimesheet.id}`,
        },
      ];
      if (this.canEdit) {
        items.push({
          label: "Удалить",
          icon: "trash",
          iconColor: "pink",
          emit: "remove",
        });
      }
      return items.filter(Boolean);
    },
    calculationsByDates() {
      const result = {};
      Object.values(this.calculations).forEach((data) => {
        const days = data.days || [];
        days.forEach((day) => {
          const key = moment(day.date, "YYYY-MM-DD").format("YYYY-MM-DD");
          if (!(key in result)) result[key] = 0;
          result[key] += 1;
        });
      });
      return result;
    },
    defaultColumns() {
      return this.columnsList.map((c) => c.code);
    },
    columns() {
      return this.columnsList.filter((c) => this.visibleColumns.includes(c.code));
    },
    serverError() {
      if (this.timesheetCreatedError) {
        const filial = this.filters.filial.label;
        const period = moment(this.filters.period);
        const month = MONTHS[period.get("month")].toLowerCase();
        const year = period.get("year");
        return `Для филиала ${filial} табель на ${month} ${year} г. уже добавлен`;
      }
      return false;
    },
    monthName() {
      if (!this.savedPeriod) return "";
      const date = moment(this.savedPeriod, "YYYY-MM-DD");
      if (date.isValid) {
        const month = date.get("month");
        return MONTHS[month];
      }
      return "";
    },

    isValid() {
      return !this.filialError;
    },

    workersList() {
      let positions = [];
      Object.values(this.workersByPositions).forEach((post) => {
        positions = [...positions, ...post];
      });
      return positions;
    },

    positionsList() {
      return this.visiblePositions.map((code) => this.positions.find((p) => p.code === code));
    },

    days() {
      const start = moment(this.savedPeriod, "YYYY-MM-DD");
      const end = start.clone().endOf("month");

      let current = start.clone();
      const days = [];

      while (current.isSameOrBefore(end)) {
        days.push({
          date: current,
          day: current.get("date"),
          key: current.format("YYYY-MM-DD"),
        });
        current = current.clone().add(1, "d");
      }

      return days;
    },

    timesheetName() {
      if (!this.currentTimesheet) return "";
      const period = moment(this.currentTimesheet.period);

      const month = MONTHS[period.get("month")].toLowerCase();
      const year = period.get("year");
      return `${this.currentTimesheet.filial.filial_name}, ${month} ${year}`;
    },

    chartData() {
      const positionsData = {};

      Object.entries(this.workersByPositions).forEach(([postCode, postData]) => {
        // расчетные данные для должности
        const postCalculations = this.calculations[postCode] ? this.calculations[postCode].days : [];

        const postChartData = postData.map((workerData) => {
          // Данные по сотруднику

          // зарплата сотрудника
          let postSalary = workerData.salaries.find((s) => s.code === postCode);
          postSalary = postSalary ? postSalary.salary : 0;

          let dayShiftsCount = 0; // кол-во дневных смен
          let nightShiftsCount = 0; // кол-во ночных смен
          let totalTimeMin = 0; // рабочее время, мин

          // показатели для реальных смен (из расчета)
          let calcDayShiftsCount = 0;
          let calcNightShiftsCount = 0;
          let calcTotalTimeMin = 0;

          // *отпуска сотрудника
          const workerVacations = workerData.vacations.map((v) => ({
            ...v,
            fromDate: moment(v.from_dt, "YYYY-MM-DD"),
            toDate: moment(v.to_dt, "YYYY-MM-DD"),
          }));

          // *смены сотрудника
          const workerTimesheet = (this.timesheet[workerData.id] && this.timesheet[workerData.id][postCode]) || [];

          // данные по дням
          const workerDays = this.days.map((day) => {
            const dayTimesheet = workerTimesheet.find((d) => d.date === day.key);

            const vacations = workerVacations.filter(
              (v) => day.date.isSameOrAfter(v.fromDate) && day.date.isSameOrBefore(v.toDate),
            );

            // Расчеты для сотрудника на текущий день
            const dayCalculations = postCalculations.filter((d) => {
              if (d.worker_id !== workerData.id) return false;
              const key = moment(d.date, "YYYY-MM-DD").format("YYYY-MM-DD");
              return key === day.key;
            });

            // let dayCalculations = (workerData.calculatedDays || []).filter(
            //   d => {
            //     let key = moment(d.date, "YYYY-MM-DD").format("YYYY-MM-DD");
            //     return key === day.key;
            //   }
            // );

            // есть ли вообще расчет на текущий день
            const hasDayCalculation = this.calculationsByDates[day.key] > 0;

            const calculatedTimesheet = new CalculatedDay(day.key, dayCalculations);

            const shiftsAndVacationsConflicts =
              ((dayTimesheet && dayTimesheet.shifts.length) ||
                (calculatedTimesheet && calculatedTimesheet.shifts.length)) &&
              vacations.length;

            const planAndFactConflicts = (() => {
              // нет плана на день для сотрудника
              if (!dayTimesheet || !dayTimesheet.shifts.length) {
                // нет расчета для сотрудника
                if (!calculatedTimesheet.shifts.length) {
                  return false; // нет конфликта
                }
                // есть расчет для сотрудника

                return true; // есть конфликт
              }

              // есть план на день для сотрудника

              // нет расчета на текущий день
              if (!hasDayCalculation) {
                return false; // нет конфликта
              }
              // нет расчета для сотрудника
              if (!calculatedTimesheet.shifts.length) {
                return true; // есть конфликт
              }
              // есть расчет для сотрудника

              // расчет совпадает с планом

              if (dayTimesheet.isSame(calculatedTimesheet)) {
                return false; // нет конфликта
              }
              // расчет не совпадает с планом
              return true; // есть конфликт
            })();

            return {
              ...day,
              timesheet: dayTimesheet, // смены в этот день
              calculatedTimesheet, // фактические смены в этот день (расчет)
              visibleTimesheet: hasDayCalculation ? calculatedTimesheet : dayTimesheet,
              totalTimeMin: dayTimesheet ? dayTimesheet.shiftsDuration : 0,
              calcTotalTimeMin: calculatedTimesheet.shiftsDuration,
              vacations, // отпуска в этот день
              hasDayCalculation,
              shiftsAndVacationsConflicts,
              planAndFactConflicts,
              hasConflict: shiftsAndVacationsConflicts || planAndFactConflicts,
            };
          });

          // *подсчет смен и рабочих часов
          workerDays.forEach((day) => {
            if (day.timesheet) {
              dayShiftsCount += day.timesheet.hasDayShift ? 1 : 0;
              nightShiftsCount += day.timesheet.hasNightShift ? 1 : 0;
              totalTimeMin += day.timesheet.shiftsDuration;
            }

            if (day.calculatedTimesheet) {
              calcDayShiftsCount += day.calculatedTimesheet.hasDayShift ? 1 : 0;
              calcNightShiftsCount += day.calculatedTimesheet.hasNightShift ? 1 : 0;
              calcTotalTimeMin += day.calculatedTimesheet.shiftsDuration;
            }
          });

          // Основная часть з/п
          const salaryMain = (postSalary / 12) * (Math.floor((totalTimeMin / 60) * 10) / 10);

          // показывать для дневной смены
          const showForDay = workerData.work_shift_type === "day" || dayShiftsCount > 0 || calcDayShiftsCount > 0;

          // показывать для ночной смены
          const showForNight =
            workerData.work_shift_type === "night" || nightShiftsCount > 0 || calcNightShiftsCount > 0;

          return {
            id: workerData.id,
            name: workerData.name,
            jobStatus: workerData.jobStatus || {},
            filial: workerData.filial,
            salary: postSalary, // оклад на данной должности
            shiftType: workerData.work_shift_type, // тип смены (дневная/ночная)
            schedule: workerData.schedule, // график работы
            days: workerDays, // данные по дням
            dayShiftsCount, // кол-во дневных смен
            nightShiftsCount, // кол-во ночных смен
            totalTimeMin, // время работы, мин
            calcDayShiftsCount, // кол-во дневных смен
            calcNightShiftsCount, // кол-во ночных смен
            calcTotalTimeMin, // время работы, мин
            salaryMain, // основная часть з/п
            calcSalaryMain: workerData.salaryMainPart,
            vacations: workerVacations, // Отпуска
            timesheet: workerTimesheet, // смены
            showForDay, // показывать для дневной смены
            showForNight, // показывать для ночной смены

            // фактические данные из расчета
            compensation: workerData.compensation || 0,
            depreciation: workerData.depreciation || 0,
            excessPremium: workerData.excessPremium || 0,
            fine: workerData.fine || 0,
            load: workerData.load || 0,
            premium: workerData.premium || 0,
            totalAccrued: workerData.totalAccrued || 0,
            penalties: parseFloat(workerData.penalties || 0),
          };
        });

        // данные по должности

        const reducedPostData = {
          salary: 0, // оклад для должности
          salaryMain: 0, // основная часть зп для должности
          totalTimeMin: 0, // рабочее время для должности, мин
          dayShiftsCount: 0, // Дневных смен для должности
          nightShiftsCount: 0, // ночных смен для должности

          calcSalaryMain: 0,
          calcTotalTimeMin: 0, // рабочее время для должности, мин
          calcDayShiftsCount: 0, // Дневных смен для должности
          calcNightShiftsCount: 0, // ночных смен для должности

          // фактические данные из расчета
          compensation: 0,
          depreciation: 0,
          excessPremium: 0,
          fine: 0,
          load: 0,
          premium: 0,
          totalAccrued: 0,
          penalties: 0,
        };

        const postDays = this.days.map((d) => ({
          ...d,
          totalTimeMin: 0,
          hasConflict: false,
        }));

        postChartData.forEach((workerChartData) => {
          Object.keys(reducedPostData).forEach((key) => {
            reducedPostData[key] += workerChartData[key];
          });

          workerChartData.days.forEach((day, i) => {
            postDays[i].totalTimeMin += day.hasDayCalculation ? day.calcTotalTimeMin : day.totalTimeMin;
            if (day.hasConflict) postDays[i].hasConflict = true;
          });
        });

        positionsData[postCode] = {
          workers: postChartData, // сотрудники на должности
          days: postDays, // данные по должности по дням
          ...reducedPostData,
        };
      });

      const reducedTotalData = {
        salary: 0, // оклад для должности
        salaryMain: 0, // основная часть зп для должности
        totalTimeMin: 0, // рабочее время для должности, мин
        dayShiftsCount: 0, // Дневных смен для должности
        nightShiftsCount: 0, // ночных смен для должности
        calcSalaryMain: 0,
        calcTotalTimeMin: 0, // рабочее время для должности, мин
        calcDayShiftsCount: 0, // Дневных смен для должности
        calcNightShiftsCount: 0, // ночных смен для должности

        // фактические данные из расчета
        compensation: 0,
        depreciation: 0,
        excessPremium: 0,
        fine: 0,
        load: 0,
        premium: 0,
        totalAccrued: 0,
        penalties: 0,
      };

      const totalDays = this.days.map((d) => ({
        ...d,
        totalTimeMin: 0,
        hasConflict: false,
      }));

      Object.values(positionsData).forEach((positionChartData) => {
        Object.keys(reducedTotalData).forEach((key) => {
          reducedTotalData[key] += positionChartData[key];
        });

        positionChartData.days.forEach((day, i) => {
          totalDays[i].totalTimeMin += day.totalTimeMin;
          if (day.hasConflict) totalDays[i].hasConflict = true;
        });
      });

      const totalData = {
        days: totalDays,
        ...reducedTotalData,
      };

      const result = {
        positions: positionsData,
        total: totalData,
      };

      return result;
    },
  },

  watch: {
    "filters.filial": function () {
      this.timesheetCreatedError = false;
    },
    "filters.period": function () {
      this.timesheetCreatedError = false;
    },
    "feedback": function (v) {
      if (v) {
        setTimeout(() => {
          this.feedback = null;
        }, 3 * 1000);
      }
    },
  },

  created() {
    if (this.$route.params.id) {
      this.timesheetDataLoading = true;
    }
    this.getEditPermission().then((canEdit) => {
      this.canEdit = canEdit;
    });
    this.$store.dispatch("timesheets/getPositions").then((items) => {
      this.positions = items;
    });

    this.loading = true;

    Promise.all([
      this.getColumnsSet().then((res) => {
        this.columnsList = res;
      }),
      this.getStartColumnsSet().then((res) => {
        this.visibleColumns = res;
      }),
    ]).then(() => {
      if (this.$route.params.id) {
        this.loadData({ id: this.$route.params.id }).then((res) => {
          this.currentTimesheet = res;

          this.filters.period = this.currentTimesheet.period;
          this.filters.filial = {
            value: this.currentTimesheet.filial.id,
            label: this.currentTimesheet.filial.filial_name,
          };

          this.savedPeriod = this.filters.period;

          this.savedTimesheet = res.days;

          this.calculations = res.calculations;

          this.timesheet = this.groupShifts(this.savedTimesheet);

          this.workersByPositions = this.formatPostsData(res.posts);

          const positions = Object.keys(this.workersByPositions);
          this.visiblePositions = this.positions.filter((p) => positions.includes(p.code)).map((p) => p.code);

          this.inited = true;
          this.loading = false;
          this.timesheetDataLoading = false;
        });
      } else {
        this.loading = false;
      }
    });
  },

  methods: {
    shiftDayOrNight(worker) {
      return (
        this.shift === "all" ||
        (this.shift === "day" && worker.showForDay) ||
        (this.shift === "night" && worker.showForNight)
      );
    },
    getColumnsSet() {
      return this.$store.dispatch("timesheets/getColumnsList", { section: "timesheet" }).then((columns) => {
        columns.sort((a, b) => a.sort - b.sort);
        return columns.map((c) => new Column(c));
      });
    },
    getStartColumnsSet() {
      return this.$store.dispatch("timesheets/getUserColumnsList", { section: "timesheet" }).then((res) => res.columns);
    },
    configColumns() {
      this.$refs.columnsModal.open(this.visibleColumns);
    },

    saveUserColumns(data) {
      this.visibleColumns = data;
      this.$store.dispatch("timesheets/saveUserColumnsList", {
        columns: data,
        section: "timesheet",
      });
    },

    getEditPermission() {
      const permissions = ["controlTimesheet", "controlTimesheetAll"];
      return this.$store.dispatch("getUserPermissions").then(() => {
        const userPermissions = this.$store.getters.getUserPermissions;
        return userPermissions.some((permission) => permissions.includes(permission));
      });
    },

    loadData(params) {
      console.log("12122")
      return Promise.all([
        this.$store.dispatch("timesheets/getData", params),
        this.$store.dispatch("timesheets/getCalculation", params),
      ]).then(([data, calculations]) => ({
        ...data,
        calculations,
      }));
    },

    groupShifts(days) {
      const workers = {};

      days.forEach((day) => {
        if (!(day.worker_id in workers)) {
          workers[day.worker_id] = {};
        }

        const worker = workers[day.worker_id];

        if (!(day.post_code in worker)) {
          worker[day.post_code] = [];
        }

        const positionShifts = worker[day.post_code];

        positionShifts.push(
          new Day({
            date: day.date,
            shifts: day.shifts.map(
              (s) =>
                new Shift({
                  type: s.shift_type,
                  fromTime: s.from_time,
                  toTime: s.to_time,
                }),
            ),
          }),
        );
      });

      return workers;
    },

    toList() {
      this.$router.push({
        name: "timesheets",
      });
    },

    getParams() {
      const params = {
        period: this.filters.period,
      };
      if (this.filters.filial) {
        params.filial_id = this.filters.filial.value;
      }
      return params;
    },

    formatPostsData(posts) {
      const result = {};

      // Сотрудники, сохраненные в табеле
      Object.entries(posts).forEach(([postCode, postData]) => {
        result[postCode] = postData.workers;
      });

      // Добавление сотрудников из ежедневных расчетов
      Object.entries(this.calculations).forEach(([postCode, postData]) => {
        const calculatedPostWorkers = {};

        postData.days.forEach((day) => {
          const { worker } = day;
          calculatedPostWorkers[worker.id] = worker;
        });

        const calculatedPostWorkersList = Object.values(calculatedPostWorkers);

        if (calculatedPostWorkersList.length) {
          // В табеле нет должности
          if (!result[postCode]) {
            result[postCode] = calculatedPostWorkersList;
          } else {
            // Добавить только новых сотрудников
            const savedPostWorkers = result[postCode];
            const savedPostWorkersIds = savedPostWorkers.map((w) => w.id);

            const newWorkers = calculatedPostWorkersList.filter((w) => !savedPostWorkersIds.includes(w.id));

            result[postCode] = [...savedPostWorkers, ...newWorkers];
          }
        }
      });

      Object.values(result).forEach((workersList) => {
        workersList.sort((a, b) => (a.name > b.name ? 1 : -1));
      });

      return result;
    },

    applyFilters() {
      this.submitted = true;
      if (!this.isValid) return;

      this.loading = true;
      this.inited = true;

      this.loadData(this.getParams()).then((data) => {
        if (data.id && (!this.currentTimesheet || data.id !== this.currentTimesheet.id)) {
          this.timesheetCreatedError = true;
          this.inited = false;
          this.loading = false;
          return;
        }

        this.currentTimesheet = data;
        this.savedPeriod = this.filters.period;

        this.savedTimesheet = data.days;
        this.timesheet = this.groupShifts(this.savedTimesheet);

        this.calculations = data.calculations;

        this.workersByPositions = this.formatPostsData(data.posts);
        this.visiblePositions = Object.keys(this.workersByPositions);

        const positions = Object.keys(this.workersByPositions);
        this.visiblePositions = this.positions.filter((p) => positions.includes(p.code)).map((p) => p.code);

        this.loading = false;
      });
    },

    resetFilters() {
      this.submitted = false;
      this.filters.filial = null;
      this.filters.period = defaultPeriod;
      this.inited = false;
    },

    openPositionsModal() {
      this.$refs.positionsModal.open({
        visible: this.visiblePositions,
      });
    },
    updatePositions(data) {
      let positions = this.visiblePositions.filter((code) => data.includes(code));

      positions = [...data.filter((code) => !this.visiblePositions.includes(code)), ...positions];

      this.visiblePositions = positions;

      const workersByPositions = {};
      Object.keys(this.workersByPositions)
        .filter((key) => data.includes(key))
        .forEach((key) => {
          workersByPositions[key] = this.workersByPositions[key];
        });
      this.workersByPositions = workersByPositions;

      const timesheet = {};

      Object.entries(this.timesheet).forEach(([workerId, workerData]) => {
        const posts = Object.keys(workerData).filter((post) => data.includes(post));

        if (posts.length) {
          timesheet[workerId] = {};

          posts.forEach((postCode) => {
            timesheet[workerId][postCode] = this.timesheet[workerId][postCode];
          });
        }
      });

      this.timesheet = timesheet;
    },
    openWorkersModal(position) {
      this.$refs.workersModal.open({
        position,
        period: this.currentTimesheet.period,
        filial: this.currentTimesheet.filial.id,
        workersList: this.workersByPositions,
      });
    },
    addWorkersToPosition(position) {
      this.openWorkersModal(position);
    },
    addWorkers({ position, workers }) {
      if (!this.visiblePositions.includes(position.code)) {
        const positions = Object.keys(this.workersByPositions);
        positions.push(position.code);
        this.visiblePositions = this.positions.filter((p) => positions.includes(p.code)).map((p) => p.code);
      }
      workers.forEach((worker) => {
        const positionList = this.workersByPositions[position.code] || [];
        positionList.push(worker);
        positionList.sort((a, b) => (a.name > b.name ? 1 : -1));
        this.workersByPositions = {
          ...this.workersByPositions,
          [position.code]: positionList,
        };
      });
    },
    removeWorker({ worker, position }) {
      // удалить из списка сотрудников
      const list = {
        ...this.workersByPositions,
      };
      const positionList = (this.workersByPositions[position.code] || []).filter(
        (w) => w.id.toString() !== worker.id.toString(),
      );
      if (!positionList.length) delete list[position.code];
      else {
        list[position.code] = positionList;
      }
      this.workersByPositions = list;

      // удалить из списка смен
      const { timesheet } = this;
      if (worker.id in timesheet) {
        const workerData = timesheet[worker.id];
        if (position.code in workerData) {
          delete workerData[position.code];
        }
        if (!Object.keys(workerData).length) {
          delete timesheet[worker.id];
        }
      }
      this.timesheet = timesheet;
    },

    getTimesheetParams() {
      const workers = [];
      const shifts = [];

      Object.entries(this.timesheet).forEach(([workerId, positions]) => {
        Object.entries(positions).forEach(([postCode, days]) => {
          days.forEach((day) => {
            if (!day.shifts.length) return;

            shifts.push({
              worker_id: workerId,
              post_code: postCode,
              date: day.date,
              shifts: day.shifts.map((s) => ({
                shift_type: s.type,
                from_time: s.fromTime,
                to_time: s.toTime,
              })),
            });
          });
        });
      });

      this.positions.forEach((position) => {
        const positionWorkers = this.workersByPositions[position.code] || [];
        positionWorkers.forEach((worker) => {
          workers.push({
            id: worker.id,
            post_code: position.code,
          });
        });
      });

      const params = {
        id: this.currentTimesheet && this.currentTimesheet.id,
        period: this.filters.period,
        filial_id: this.filters.filial.value,
        workers,
        days: shifts,
      };

      return params;
    },

    save() {
      this.saving = true;
      this.$store.dispatch("timesheets/saveTimesheet", this.getTimesheetParams()).then((res) => {
        if (res.success) {
          const period = moment(this.filters.period);
          const month = MONTHS[period.get("month")];
          const year = period.get("year");

          this.currentTimesheet = res.model;

          this.feedback = {
            status: "success",
            text: `Табель на ${month} ${year} успешно сохранен`,
          };

          this.$router
            .push({
              name: "edit-timesheet",
              params: {
                id: res.model.id,
              },
            })
            .catch(() => {});
        } else {
          this.feedback = {
            status: "error",
            text: "Произошла ошибка при сохранении",
          };
        }
        this.saving = false;
      });
    },

    reset() {},

    onDayClick(data) {
      this.$refs.shiftModal.open({
        hasCalculation: data.day.hasDayCalculation,
        calculatedShifts: (data.day.calculatedTimesheet && data.day.calculatedTimesheet.shifts) || [],
        date: data.day.key,
        worker: data.worker,
        position: data.position,

        filial: this.currentTimesheet.filial.filial_name,
      });
    },

    saveShifts(data) {
      const worker = this.timesheet[data.worker.id] || {};
      let positionShifts = worker[data.position.code] || [];

      const selectedDays = data.days;

      positionShifts = [
        ...positionShifts.filter((day) => !selectedDays.includes(day.date)),
        ...selectedDays.map(
          (date) =>
            new Day({
              date,
              shifts: data.shifts.map((s) => new Shift(s)),
            }),
        ),
      ];

      this.timesheet = {
        ...this.timesheet,
        [data.worker.id]: {
          ...worker,
          [data.position.code]: positionShifts,
        },
      };
    },

    removeTimesheet() {
      this.$refs.removeTimesheetModal.open(new Timesheet(this.currentTimesheet));
    },

    onRemove() {
      this.$router.push({
        name: "timesheets",
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import "~@/assets/styles/vars";
.layout {
  &-header {
    height: auto;
  }
}
.timesheet {
  .timesheet-total-column__days {
    padding: 0;
    max-width: none!important;
    height: 1px;
    .cell {
      height: 100%;
    }
  }
  .timesheet-total-column__worker {
    z-index: 2;
  }
  .layout-title {
    font-weight: 400;
    b {
      font-weight: 500;
    }
  }

  &-chart {
    margin-top: 30px;

    .h-table {
      min-width: 0;
    }

    &-column {
      width: 170px;
      min-width: 170px;
      max-width: 170px;

      &__days {
        padding-left: 0;
        padding-right: 0;
        max-width: none;

        .h-table__col-title {
          padding-left: 8px;
          padding-right: 8px;
        }
      }
    }
    &__actions {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-left: -5px;
      margin-right: -5px;
    }
    &__actions-group {
      display: flex;
      align-items: center;
    }
    &__action {
      margin-left: 5px;
      margin-right: 5px;
      margin-bottom: 20px;
    }

    &__table {
      th {
        min-width: 75px;

        &.timesheet-chart-column__worker {
          position: relative;
          &:after {
            content: "";
            position: absolute;
            top: -1px;
            bottom: -1px;
            right: 0;
            left: -1px;
            //border-top: 1px solid #f5f7fa;
            border-bottom: 1px solid #f5f7fa;
            pointer-events: none;
          }
        }
      }
      thead.fixed {
        z-index: 3;
      }
      tfoot tr {
        background: #f5f7fa;
      }
      .position-name {
        background: #f5f7fa;

        &__worker {
          z-index: 2;
          border-right: 1px solid #f5f7fa !important;
        }

        &__loadType {
          border-left-color: transparent;
          border-right-color: transparent;
        }
      }
      .timeline {
        display: flex;
        height: 40px;

        margin-right: -1px;
        margin-bottom: -9px;
      }
      .day {
        width: 24px;
        flex-shrink: 0;
        border: 1px solid #dfe4ef;
        margin-left: -1px;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;

        &:last-child {
          border-right-color: transparent;
        }

        &:before {
          content: "";
          position: absolute;
          top: -1px;
          left: -2px;
          right: -2px;
          bottom: -1px;
          border: 2px solid transparent;
          z-index: 1;
          border-left-color: transparent;
          border-right-color: transparent;
        }

        &.conflict {
          position: relative;
          &:before {
            border-top-color: #ff238d;
            border-bottom-color: #ff238d;
          }
          &:last-child:before {
            border-right-color: #ff238d;
          }
        }

        &:not(.conflict) + .conflict {
          &:before {
            border-left-color: #ff238d;
          }
        }

        &.conflict + .day:not(.conflict) {
          &:before {
            border-left-color: #ff238d;
            left: -1px;
          }
        }
      }
      .worker-data {
        &__worker {
          width: 170px;
          min-width: 170px;
          max-width: 170px;
          background: white;
          z-index: 2;

          &::after {
            position: absolute;
            content: "";
            pointer-events: none;
            top: -1px;
            left: -1px;
            right: 0;
            bottom: -1px;
            border-top: 1px solid #dfe4ef;
            border-bottom: 1px solid #dfe4ef;
          }
        }
        &__days {
          padding: 0;
          height: 1px;
          min-height: 0;
          .cell {
            height: 100%;
          }
        }
        &__premium,
        &__fine,
        &__totalAccrued {
          height: 0;
          padding: 0;

          .cell {
            height: 100%;
            padding: 8px;
            display: flex;
            align-items: center;
          }
        }
      }
      & /deep/ {
        .cell {
          justify-content: flex-end;

          &__worker {
            justify-content: flex-start;
          }
          &__loadType {
            justify-content: flex-start;
          }
          &__totalShifts {
            .shifts {
              div {
                width: 100%;
                position: relative;
                padding-left: 20px;
                svg {
                  position: absolute;
                  top: 0;
                  bottom: 0;
                  left: 0;
                  margin: auto;
                }
              }
            }
          }

          .salary-main {
            text-align: right;
          }
        }
      }
    }
  }
  &__actions {
    margin-top: 25px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
  &__reset {
    margin-right: 10px;
  }
  & /deep/ {
    .c-button__dropdown {
      &__container {
        max-width: none;
        &__list__item__label {
          white-space: nowrap;
        }
      }
    }
  }
  & /deep/ .ps__rail-x {
    z-index: 4;
  }
  &-feedback {
    position: fixed;
    z-index: 100;
    top: 20px;
    right: 20px;
    min-height: 60px;
    box-shadow: 0px 4px 16px rgba(0, 15, 38, 0.25);
    border-radius: 6px;
    padding: 21px 28px;
    font-size: 13px;
    line-height: 1.2;

    &__wrapper {
      display: flex;
      align-items: center;
    }

    &__icon {
      margin-right: 12px;
    }

    &.success {
      background: #d0f4e3;
    }

    &.error {
      background: #ffe6f2;
    }
  }
}

@include mobileAndTablet {
  .layout {
    padding-bottom: 100px;
  }
  & /deep/ {
    .timesheet-filters {
      &__period {
        width: 185px;
      }
      &__filial {
        width: 246px;
      }
    }
  }

  .scrolled-table {
    margin-right: -30px;

    &:before {
      content: "";
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      width: 15px;
      background: red;
      z-index: 5;
      background: linear-gradient(270deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%);
    }

    & /deep/ {
      th:last-child,
      td:last-child {
        position: relative !important;

        &:before {
          display: none;
        }
      }

      .scrolled-table__container {
        padding-right: 30px;
      }
    }
  }

  .timesheet-chart {
    &__shifts {
      & /deep/ {
        .c-switcher-group-item {
          padding: 0;
          width: 36px;

          .c-switcher-group-item-label {
            font-size: 0;
            &:before {
              content: "";
              width: 14px;
              height: 14px;
              background-position: center;
              background-repeat: no-repeat;
              display: block;
            }
          }

          &:first-child {
            .c-switcher-group-item-label {
              &:before {
                background-image: url("data:image/svg+xml,%3Csvg width='14' height='12' viewBox='0 0 14 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M3 6C3 7.65685 4.34315 9 6 9V11M3 6C3 4.34315 4.34315 3 6 3V1M3 6H1M2.46448 9.53552L3.50003 8.49997M3.50003 3.5L2.46449 2.46445M8.75195 4.25753C9.75131 3.11594 11.4296 2.91122 12.7035 3.70345C11.8052 4.25753 11.0269 5.05434 11.0268 6.24261C11.0268 7.4309 11.8052 8.22775 12.7035 8.78183C11.4296 9.57406 9.75131 9.36935 8.75195 8.22775C8.25409 7.65903 7.9855 7.07363 7.9855 6.24261C7.98551 5.41162 8.25409 4.82625 8.75195 4.25753Z' stroke='%238E9CBB' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
              }
            }
          }
          &:nth-child(2) {
            .c-switcher-group-item-label {
              &:before {
                background-image: url("data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9 6C9 7.65685 7.65685 9 6 9M9 6C9 4.34315 7.65685 3 6 3M9 6H11M6 9C4.34315 9 3 7.65685 3 6M6 9V11M3 6C3 4.34315 4.34315 3 6 3M3 6H1M6 3V1M8.5 3.5L9.53555 2.46445M2.46448 9.53552L3.50003 8.49997M3.50003 3.5L2.46449 2.46445M9.53555 9.53552L8.5 8.49997' stroke='%238E9CBB' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E%0A");
              }
            }
          }
          &:last-child {
            .c-switcher-group-item-label {
              &:before {
                background-image: url("data:image/svg+xml,%3Csvg width='10' height='10' viewBox='0 0 10 10' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M4.21206 1C2.26415 1.45407 0.874839 3.22939 1.00894 5.24791C1.07574 6.25349 1.37442 7.05859 2.15788 7.84207C2.94136 8.62557 3.74651 8.92426 4.75209 8.99106C6.77061 9.12516 8.54593 7.73585 9 5.78794C7.63077 6.11239 6.1456 6.095 5.02529 4.97466C3.90499 3.85434 3.88761 2.36924 4.21206 1Z' stroke='%238E9CBB' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
              }
            }
          }

          &.c-switcher-group-item_active {
            &:first-child {
              .c-switcher-group-item-label {
                &:before {
                  background-image: url("data:image/svg+xml,%3Csvg width='14' height='12' viewBox='0 0 14 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M3 6C3 7.65685 4.34315 9 6 9V11M3 6C3 4.34315 4.34315 3 6 3V1M3 6H1M2.46448 9.53552L3.50003 8.49997M3.50003 3.5L2.46449 2.46445M8.75195 4.25753C9.75131 3.11594 11.4296 2.91122 12.7035 3.70345C11.8052 4.25753 11.0269 5.05434 11.0268 6.24261C11.0268 7.4309 11.8052 8.22775 12.7035 8.78183C11.4296 9.57406 9.75131 9.36935 8.75195 8.22775C8.25409 7.65903 7.9855 7.07363 7.9855 6.24261C7.98551 5.41162 8.25409 4.82625 8.75195 4.25753Z' stroke='%23333333' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
                }
              }
            }
            &:nth-child(2) {
              .c-switcher-group-item-label {
                &:before {
                  background-image: url("data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9 6C9 7.65685 7.65685 9 6 9M9 6C9 4.34315 7.65685 3 6 3M9 6H11M6 9C4.34315 9 3 7.65685 3 6M6 9V11M3 6C3 4.34315 4.34315 3 6 3M3 6H1M6 3V1M8.5 3.5L9.53555 2.46445M2.46448 9.53552L3.50003 8.49997M3.50003 3.5L2.46449 2.46445M9.53555 9.53552L8.5 8.49997' stroke='%23333333' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E%0A");
                }
              }
            }
            &:last-child {
              .c-switcher-group-item-label {
                &:before {
                  background-image: url("data:image/svg+xml,%3Csvg width='10' height='10' viewBox='0 0 10 10' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M4.21206 1C2.26415 1.45407 0.874839 3.22939 1.00894 5.24791C1.07574 6.25349 1.37442 7.05859 2.15788 7.84207C2.94136 8.62557 3.74651 8.92426 4.75209 8.99106C6.77061 9.12516 8.54593 7.73585 9 5.78794C7.63077 6.11239 6.1456 6.095 5.02529 4.97466C3.90499 3.85434 3.88761 2.36924 4.21206 1Z' stroke='%23333333' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
                }
              }
            }
          }
        }
      }
    }

    &__config-columns {
      width: 30px;
      padding: 0;
      & /deep/ {
        .c-button-icon {
          margin: 0;
        }
        .c-button-label {
          display: none;
        }
      }
    }
  }
}

@include onlyMobile {
  .timesheet {
    padding-top: 25px;
    padding-bottom: 0px !important;
    .layout-header {
      margin-bottom: 20px;
      align-items: center;

      &-actions {
        margin: 0 -5px;
      }
      &-action {
        margin: 0 5px;
      }
    }

    &__actions {
      margin-left: -21px;
      margin-right: -21px;
      position: sticky;
      bottom: 0;
      z-index: 20;
      background: white;
      box-shadow: 0px 0px 15px rgba(8, 20, 45, 0.3);

      .timesheet__reset,
      .timesheet__save {
        width: 50%;
        margin: 0;
        border-radius: 0;
        & /deep/ {
        }
      }
    }
  }
  & /deep/ {
    .timesheet-filters {
      margin-top: 20px;
      &__row {
        flex-direction: column;
      }
      &__filters {
        width: 100%;
      }
      &__controls {
        display: flex;
        flex-direction: column;
      }
      &__period {
        width: 185px;
      }
      &__filial {
        width: 246px;
        min-width: 0;
        margin-top: 24px;
      }
      &__actions {
        margin-top: 10px;
      }
    }
  }

  #pagetop {
    bottom: 50px;
  }

  .timesheet-chart {
    &__add-workers {
      padding: 0 !important;
      width: 30px;

      & /deep/ {
        .c-button {
          &-label {
            font-size: 0;

            &:before {
              content: "";
              display: block;
              width: 18px;
              height: 18px;
              margin-left: 1px;
              background-repeat: no-repeat;
              background-image: url("data:image/svg+xml,%3Csvg width='17' height='18' viewBox='0 0 17 18' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M14.0264 16.066C14.0264 16.6183 14.4741 17.066 15.0264 17.066C15.5786 17.066 16.0264 16.6183 16.0264 16.066H14.0264ZM11.2874 10.9898V9.98982V10.9898ZM4.77509 10.9898V9.98982V10.9898ZM1.03613 14.978H0.0361328H1.03613ZM0.0361328 16.066C0.0361328 16.6183 0.483848 17.066 1.03613 17.066C1.58842 17.066 2.03613 16.6183 2.03613 16.066H0.0361328ZM16.0264 16.066V14.978H14.0264V16.066H16.0264ZM16.0264 14.978C16.0264 13.6752 15.5417 12.4137 14.6608 11.474L13.2017 12.8419C13.7231 13.3981 14.0264 14.1654 14.0264 14.978H16.0264ZM14.6608 11.474C13.7778 10.5321 12.5658 9.98982 11.2874 9.98982V11.9898C11.9922 11.9898 12.6823 12.2879 13.2017 12.8419L14.6608 11.474ZM11.2874 9.98982H4.77509V11.9898H11.2874V9.98982ZM4.77509 9.98982C3.49666 9.98982 2.28472 10.5321 1.40171 11.474L2.86079 12.8419C3.38016 12.2879 4.07026 11.9898 4.77509 11.9898V9.98982ZM1.40171 11.474C0.520752 12.4137 0.0361328 13.6752 0.0361328 14.978H2.03613C2.03613 14.1654 2.33936 13.3981 2.86079 12.8419L1.40171 11.474ZM0.0361328 14.978V16.066H2.03613V14.978H0.0361328ZM9.99116 3.99471C9.99116 5.0944 9.09969 5.98588 7.99999 5.98588V7.98588C10.2043 7.98588 11.9912 6.19897 11.9912 3.99471H9.99116ZM7.99999 5.98588C6.9003 5.98588 6.00883 5.0944 6.00883 3.99471H4.00883C4.00883 6.19897 5.79573 7.98588 7.99999 7.98588V5.98588ZM6.00883 3.99471C6.00883 2.89502 6.9003 2.00354 7.99999 2.00354V0.00354004C5.79573 0.00354004 4.00883 1.79045 4.00883 3.99471H6.00883ZM7.99999 2.00354C9.09969 2.00354 9.99116 2.89502 9.99116 3.99471H11.9912C11.9912 1.79045 10.2043 0.00354004 7.99999 0.00354004V2.00354Z' fill='%238E9CBB'/%3E%3C/svg%3E%0A");
            }
          }
        }
      }
    }
    &__config-positions {
      padding: 0 !important;
      width: 30px;

      & /deep/ {
        .c-button {
          &-label {
            font-size: 0;

            &:before {
              content: "";
              display: block;
              width: 18px;
              height: 18px;
              margin-left: 1px;
              background-repeat: no-repeat;
              background-image: url("data:image/svg+xml,%3Csvg width='18' height='16' viewBox='0 0 18 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='1' y='3' width='16' height='12' rx='2' stroke='%238E9CBB' stroke-width='2'/%3E%3Crect x='4' y='7' width='4' height='5' rx='1' fill='%238E9CBB'/%3E%3Cpath d='M9 5V1' stroke='%238E9CBB' stroke-width='2' stroke-linecap='round'/%3E%3Cpath d='M13 8H11' stroke='%238E9CBB' stroke-width='2' stroke-linecap='round'/%3E%3Cpath d='M13 11H11' stroke='%238E9CBB' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E%0A");
            }
          }
        }
      }
    }
    &__config-codes {
      & /deep/ {
        .c-button {
          padding: 0 !important;
          width: 30px;

          &-label {
            font-size: 0;

            &:before {
              content: "";
              display: block;
              width: 16px;
              height: 14px;
              margin-top: 4px;
              background-repeat: no-repeat;
              background-image: url("data:image/svg+xml,%3Csvg width='16' height='12' viewBox='0 0 16 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M15 7.44444V3C11.5 3 10 3.57143 10 5.28571C10 7 11.5 7.44444 15 7.44444ZM15 7.44444V11M13 7.44444L10 11' stroke='%238E9CBB' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M1 8L4 1L7 8M1.85714 6H6.14286' stroke='%238E9CBB' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
            }
          }
        }
      }
    }
  }

  .scrolled-table {
    margin-left: -21px;
    margin-right: -21px;

    & /deep/ {
      .scrolled-table__container {
        padding-right: 20px;
      }
    }
  }

  .layout {
    &-header-actions {
      padding-top: 4px;
    }
    &-header {
      .actions {
        & /deep/ {
          .c-button__wrapper {
            padding: 0;
            width: 30px;
          }
          .c-button__label__left {
            display: none;
          }
          .c-button__icon__right {
            margin-left: 4px;
          }
        }
      }
    }
  }
}
</style>

<style>
.h-table th, .h-table td {
  overflow: inherit!important;
}
</style>